import { useState, useEffect, useCallback } from 'react';
import { Calendar, MapPin, Ticket } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_IMG = "https://images.unsplash.com/photo-1681731030409-c4448f48a701?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBjbGFzc2ljYWwlMjBtdXNpYyUyMHBlcmZvcm1hbmNlfGVufDB8fHx8MTc3NTg1NTc4OHww&ixlib=rb-4.1.0&q=85";

const BADGE_MAP = {
  concert: "badge-concert",
  workshop: "badge-workshop",
  festival: "badge-festival",
};

function EventCard({ event }) {
  return (
    <div
      data-testid={`event-card-${event.id}`}
      className="bg-[#122036] border border-[#d4af37]/20 rounded-xl p-6 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(212,175,55,0.1)] transition-all duration-300"
    >
      <div className="flex items-start justify-between mb-3">
        <span className={`text-xs uppercase tracking-wider px-3 py-1 rounded-full ${BADGE_MAP[event.event_type] || 'badge-concert'}`}>
          {event.event_type}
        </span>
        {event.date && (
          <span className="flex items-center gap-1 text-[#a3b1c6] text-xs">
            <Calendar className="w-3 h-3" />
            {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
      </div>
      <h3 className="font-['Cormorant_Garamond'] text-xl text-[#fdfbf7] mb-2">{event.title}</h3>
      <p className="text-[#a3b1c6] text-sm mb-3 line-clamp-2">{event.description}</p>
      {event.location && (
        <p className="flex items-center gap-1 text-[#d4af37] text-xs">
          <MapPin className="w-3 h-3" /> {event.location}
        </p>
      )}
    </div>
  );
}

function NewsCard({ item }) {
  return (
    <div
      data-testid={`news-card-${item.id}`}
      className="bg-[#122036] border border-[#d4af37]/20 rounded-xl p-6 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(212,175,55,0.1)] transition-all duration-300"
    >
      <div className="flex items-center gap-3 mb-2">
        <span className="inline-block bg-[#1c2a3d] border border-[#d4af37]/30 text-[#d4af37] text-xs uppercase tracking-wider px-3 py-1 rounded-full">
          {item.category}
        </span>
        <span className="text-[#a3b1c6] text-xs">{item.published_date}</span>
      </div>
      <h3 className="font-['Cormorant_Garamond'] text-xl text-[#fdfbf7] mb-2">{item.title}</h3>
      <p className="text-[#a3b1c6] text-sm line-clamp-2">{item.content}</p>
    </div>
  );
}

export default function EventsSection() {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);

  const fetchData = useCallback(() => {
    axios.get(`${API}/events`).then(r => setEvents(r.data)).catch(() => {});
    axios.get(`${API}/news`).then(r => setNews(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <section id="events" data-testid="events-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[#d4af37] mb-4 flex items-center gap-2">
              <Ticket className="w-4 h-4" /> Upcoming Events
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#fdfbf7] mb-8 tracking-tight">Live Experiences</h2>
            {events.length === 0 ? (
              <p className="text-[#a3b1c6]">No upcoming events</p>
            ) : (
              <div className="space-y-4">
                {events.map(event => <EventCard key={event.id} event={event} />)}
              </div>
            )}
          </div>

          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[#d4af37] mb-4">Latest News</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#fdfbf7] mb-8 tracking-tight">Sitar World</h2>
            <div className="relative rounded-xl overflow-hidden border border-[#d4af37]/20 mb-6">
              <img src={EVENT_IMG} alt="Classical performance" className="w-full h-48 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] to-transparent" />
            </div>
            {news.length === 0 ? (
              <p className="text-[#a3b1c6]">No news yet</p>
            ) : (
              <div className="space-y-4">
                {news.map(item => <NewsCard key={item.id} item={item} />)}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="section-divider mt-24 md:mt-32 max-w-4xl mx-auto" />
    </section>
  );
}
